import React from "react";
import { trim } from "lodash";

type ErrorMessages = {
  characterError: React.ReactNode;
  urlError: React.ReactNode;
};

export type SocialMediaConfigItem = {
  key: string;
  label: string;
  icon: string;
  iconClassName: string;
  domain: string;
  exceptions?: string[];
  errorMessages: ErrorMessages;
};

type SocialMediaNormalizerArgs = {
  input: string;
  domain: string;
  exceptions?: string[];
  errorMessages?: ErrorMessages;
};

type SocialMediaNormalizerResult =
  | { success: string | null }
  | { error: React.ReactNode | string };

const defaultErrorMessages: ErrorMessages = {
  characterError: (
    <span>Unable to save link. Please remove special symbols.</span>
  ),
  urlError: <span>This link must contain the correct domain.</span>,
};

export const SOCIAL_MEDIA_CONFIG: SocialMediaConfigItem[] = [
  {
    key: "facebook",
    label: "Facebook",
    icon: "fa fa-facebook",
    iconClassName: "fa-facebook",
    domain: "facebook.com",
    errorMessages: {
      characterError: <span>Unable to save Facebook link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>facebook.com</b>
        </span>
      ),
    },
  },
  {
    key: "twitter",
    label: "X",
    icon: "fab fa6-x-twitter",
    iconClassName: "fab fa6-x-twitter",
    domain: "x.com",
    exceptions: ["twitter.com"],
    errorMessages: {
      characterError: <span>Unable to save X link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>x.com</b> or <b>twitter.com</b>
        </span>
      ),
    },
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    icon: "fa fa-linkedin",
    iconClassName: "fa-linkedin",
    domain: "linkedin.com/in",
    exceptions: ["linkedin.com/company"],
    errorMessages: {
      characterError: <span>Unable to save LinkedIn link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>linkedin.com</b>
        </span>
      ),
    },
  },
  {
    key: "instagram",
    label: "Instagram",
    icon: "fa fa-instagram",
    iconClassName: "fa-instagram",
    domain: "instagram.com",
    errorMessages: {
      characterError: <span>Unable to save Instagram link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>instagram.com</b>
        </span>
      ),
    },
  },
  {
    key: "youtube",
    label: "YouTube",
    icon: "fab fa6-youtube",
    iconClassName: "fa-youtube-play",
    domain: "youtube.com",
    exceptions: ["youtube.com/channel", "youtube.com/c", "youtube.com/user"],
    errorMessages: {
      characterError: <span>Unable to save YouTube link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>youtube.com</b>
        </span>
      ),
    },
  },
  {
    key: "pinterest",
    label: "Pinterest",
    icon: "fab fa6-pinterest",
    iconClassName: "fa-pinterest-p",
    domain: "pinterest.com",
    errorMessages: {
      characterError: <span>Unable to save Pinterest link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>pinterest.com</b>
        </span>
      ),
    },
  },
  {
    key: "tiktok",
    label: "TikTok",
    icon: "fab fa6-tiktok",
    iconClassName: "fa-tiktok",
    domain: "tiktok.com",
    errorMessages: {
      characterError: <span>Unable to save TikTok link. Please remove special symbols.</span>,
      urlError: (
        <span>
          This link must contain <b>tiktok.com</b>
        </span>
      ),
    },
  },
];

const socialMediaNormalizer = ({
  input,
  domain,
  exceptions = [],
  errorMessages = defaultErrorMessages,
}: SocialMediaNormalizerArgs): SocialMediaNormalizerResult => {
  const value = trim(input);
  const cleanDomain = trim(domain).toLowerCase();
  const cleanExceptions = exceptions.map((e) => trim(e).toLowerCase());

  const mainMatch = cleanDomain.match(/^([^/]+)(\/.*)?$/);
  const main = mainMatch
    ? { host: mainMatch[1], basePath: mainMatch[2] || "" }
    : null;

  if (!main) return { error: "Invalid domain configuration" };

  const urlRegex = /^(?:https?:\/\/)?([^/]+)(\/.*)?$/i;
  const urlMatch = value.match(urlRegex);

  if (urlMatch) {
    const hostname = urlMatch[1].toLowerCase();
    const path = urlMatch[2] || "";

    const matchedException = cleanExceptions
      .map((exception) => {
        const match = exception.match(/^([^/]+)(\/.*)?$/);
        return match
          ? { host: match[1], basePath: match[2] || "" }
          : null;
      })
      .find((ex) => {
        if (!ex) return false;
        const validHost =
          hostname === ex.host || hostname.endsWith(`.${ex.host}`);
        return validHost && path.startsWith(ex.basePath);
      });

    if (matchedException) {
      return {
        success: `https://${matchedException.host}${path}`,
      };
    }

    const validMainHost =
      hostname === main.host || hostname.endsWith(`.${main.host}`);

    if (validMainHost) {
      return {
        success: `https://${main.host}${main.basePath}${path.replace(
          main.basePath,
          ""
        )}`,
      };
    }

    // Only treat as an invalid domain if it looks like a real domain
    // (ends with a TLD-like suffix of 2+ letters). This allows usernames
    // like "brian.bruns.3" to pass through as valid usernames.
    const lastDotIndex = hostname.lastIndexOf(".");
    const potentialTld = hostname.substring(lastDotIndex + 1);
    const looksLikeDomain = /^[a-z]{2,}$/i.test(potentialTld);

    if (hostname.includes(".") && looksLikeDomain) {
      return { error: errorMessages.urlError };
    }
  }

  const invalidCharRegex = /[()#*$]/;
  if (invalidCharRegex.test(value)) {
    return { error: errorMessages.characterError };
  }

  return {
    success:
      value === "" ? null : `https://${main.host}${main.basePath}/${value}`,
  };
};

export default socialMediaNormalizer;
